import { useForm, useFieldArray, FieldErrors } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { MapPin } from "lucide-react";
import { lojaSchema, LojaFormData } from "../../../lib/validations";
import FormInput from "../../../components/FormInput";
import MaskedInput from "../../../components/MaskedInput";
import FormActions from "../../../components/Form/FormActions";
import CustomSwitch from "../../../components/Switch";
import { REGRAS_LOTE_ROTEIRIZACAO_DEFAULT } from "../../../types/loja";
import { roteirizacaoService } from "../../../services/roteirizacaoService";

type LojaTab = "geral" | "roteirista";

interface LojaFormProps {
  initialData?: LojaFormData;
  onSubmit: (data: LojaFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const emptyDefaults: LojaFormData = {
  nome: "",
  endereco: "",
  telefone: "",
  cnpj: "",
  usaIntegracaoPedidos: false,
  roteirizacaoAutomaticaAtiva: false,
  distanciaMaximaCasamentoKm: 2,
  aguardarCasamentoMinutos: 1,
  lojaLatitude: null,
  lojaLongitude: null,
  regrasLoteRoteirizacao: REGRAS_LOTE_ROTEIRIZACAO_DEFAULT,
};

const TABS: { id: LojaTab; label: string }[] = [
  { id: "geral", label: "Geral" },
  { id: "roteirista", label: "Roteirista automático" },
];

const LojaForm = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading,
}: LojaFormProps) => {
  const [activeTab, setActiveTab] = useState<LojaTab>("geral");
  const [geocoding, setGeocoding] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm<LojaFormData>({
    resolver: zodResolver(lojaSchema),
    defaultValues: emptyDefaults,
  });

  const { fields } = useFieldArray({
    control,
    name: "regrasLoteRoteirizacao",
  });

  const usaIntegracaoPedidos = watch("usaIntegracaoPedidos");
  const roteirizacaoAutomaticaAtiva = watch("roteirizacaoAutomaticaAtiva");
  const endereco = watch("endereco");

  useEffect(() => {
    setActiveTab("geral");
    if (initialData) {
      reset({
        ...emptyDefaults,
        ...initialData,
        usaIntegracaoPedidos: initialData.usaIntegracaoPedidos ?? false,
        roteirizacaoAutomaticaAtiva:
          initialData.roteirizacaoAutomaticaAtiva ?? false,
        distanciaMaximaCasamentoKm:
          initialData.distanciaMaximaCasamentoKm ?? 2,
        aguardarCasamentoMinutos: initialData.aguardarCasamentoMinutos ?? 1,
        lojaLatitude: initialData.lojaLatitude ?? null,
        lojaLongitude: initialData.lojaLongitude ?? null,
        regrasLoteRoteirizacao:
          initialData.regrasLoteRoteirizacao?.length
            ? initialData.regrasLoteRoteirizacao
            : REGRAS_LOTE_ROTEIRIZACAO_DEFAULT,
      });
    } else {
      reset(emptyDefaults);
    }
  }, [initialData, reset]);

  const handleFormSubmit = (data: LojaFormData) => {
    const latitude =
      data.lojaLatitude === undefined ||
      data.lojaLatitude === null ||
      Number.isNaN(Number(data.lojaLatitude))
        ? null
        : Number(data.lojaLatitude);
    const longitude =
      data.lojaLongitude === undefined ||
      data.lojaLongitude === null ||
      Number.isNaN(Number(data.lojaLongitude))
        ? null
        : Number(data.lojaLongitude);

    onSubmit({
      ...data,
      distanciaMaximaCasamentoKm: Number(data.distanciaMaximaCasamentoKm ?? 2),
      aguardarCasamentoMinutos: Number(data.aguardarCasamentoMinutos ?? 1),
      lojaLatitude: latitude,
      lojaLongitude: longitude,
      regrasLoteRoteirizacao: data.regrasLoteRoteirizacao ?? null,
    });
  };

  const handleInvalid = (formErrors: FieldErrors<LojaFormData>) => {
    const geralKeys: (keyof LojaFormData)[] = [
      "nome",
      "endereco",
      "telefone",
      "cnpj",
      "usaIntegracaoPedidos",
    ];
    const hasGeralError = geralKeys.some((key) => Boolean(formErrors[key]));
    setActiveTab(hasGeralError ? "geral" : "roteirista");
    toast.error("Revise os campos destacados antes de salvar.");
  };

  const handleGeocodeEndereco = async () => {
    const enderecoLimpo = (endereco ?? "").trim();
    if (enderecoLimpo.length < 5) {
      toast.error("Preencha um endereço válido na aba Geral antes de buscar.");
      setActiveTab("geral");
      return;
    }

    setGeocoding(true);
    try {
      const result = await roteirizacaoService.geocodeEndereco(enderecoLimpo);
      setValue("lojaLatitude", result.latitude, { shouldDirty: true });
      setValue("lojaLongitude", result.longitude, { shouldDirty: true });
      toast.success(
        result.formattedAddress
          ? `Coordenadas preenchidas: ${result.formattedAddress}`
          : "Coordenadas preenchidas pelo Google."
      );
    } catch (error: unknown) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Falha ao buscar coordenadas no Google."
      );
    } finally {
      setGeocoding(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit, handleInvalid)}
      className="flex min-h-0 flex-1 flex-col"
    >
      <div className="mb-4 flex flex-wrap gap-2">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                isActive
                  ? "bg-primary text-white shadow-sm"
                  : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200 hover:text-neutral-800"
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain pr-1">
        {activeTab === "geral" && (
          <>
            <FormInput
              label="Nome"
              registration={register("nome")}
              error={errors.nome?.message}
            />

            <FormInput
              label="Endereço"
              registration={register("endereco")}
              error={errors.endereco?.message}
            />

            <MaskedInput
              label="Telefone"
              mask="(99) 99999-9999"
              placeholder="(11) 99999-9999"
              registration={register("telefone")}
              error={errors.telefone?.message}
            />

            <MaskedInput
              label="CNPJ"
              mask="99.999.999/9999-99"
              placeholder="00.000.000/0000-00"
              registration={register("cnpj")}
              error={errors.cnpj?.message}
            />

            <CustomSwitch
              label="Usar integração de pedidos (exige número do pedido no Roteirista)"
              checked={!!usaIntegracaoPedidos}
              onChange={(checked) => setValue("usaIntegracaoPedidos", checked)}
            />
          </>
        )}

        {activeTab === "roteirista" && (
          <div className="space-y-4">
            <p className="text-sm text-neutral-500">
              Casamento de comandas por proximidade (linha reta) e faixas de
              horário. O geocode dos pedidos usa o endereço; lat/lng da loja é
              opcional.
            </p>

            <CustomSwitch
              label="Ativar roteirização automática"
              checked={!!roteirizacaoAutomaticaAtiva}
              onChange={(checked) =>
                setValue("roteirizacaoAutomaticaAtiva", checked)
              }
            />

            <FormInput
              label="Distância máxima entre comandas (km)"
              type="number"
              step="0.01"
              min={0.1}
              max={50}
              registration={register("distanciaMaximaCasamentoKm", {
                valueAsNumber: true,
              })}
              error={errors.distanciaMaximaCasamentoKm?.message}
            />

            <FormInput
              label="Tempo de espera para casar (minutos)"
              type="number"
              step="0.5"
              min={0}
              max={30}
              registration={register("aguardarCasamentoMinutos", {
                valueAsNumber: true,
              })}
              error={errors.aguardarCasamentoMinutos?.message}
            />
            <p className="-mt-2 text-xs text-neutral-500">
              Após ficar pronta, a comanda espera esse tempo antes de liberar
              sozinha — para tentar casar com outra. Se encher a capacidade do
              horário, libera na hora.
            </p>

            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-neutral-500">
                  Usa o endereço da aba Geral para preencher lat/lng via Google.
                </p>
                <button
                  type="button"
                  onClick={handleGeocodeEndereco}
                  disabled={geocoding || isLoading}
                  className="inline-flex items-center gap-1.5 rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-xs font-medium text-neutral-700 transition hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <MapPin className="h-3.5 w-3.5" />
                  {geocoding ? "Buscando…" : "Buscar no Google"}
                </button>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <FormInput
                  label="Latitude da loja (opcional)"
                  type="number"
                  step="any"
                  registration={register("lojaLatitude", {
                    setValueAs: (v) =>
                      v === "" || v === null || v === undefined
                        ? null
                        : Number(v),
                  })}
                  error={errors.lojaLatitude?.message}
                />
                <FormInput
                  label="Longitude da loja (opcional)"
                  type="number"
                  step="any"
                  registration={register("lojaLongitude", {
                    setValueAs: (v) =>
                      v === "" || v === null || v === undefined
                        ? null
                        : Number(v),
                  })}
                  error={errors.lojaLongitude?.message}
                />
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-xs font-medium text-neutral-600">
                Faixas horárias (máx. comandas por lote)
              </p>
              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className="grid grid-cols-3 gap-2 rounded-md bg-neutral-50 p-2"
                >
                  <FormInput
                    label="Início"
                    registration={register(
                      `regrasLoteRoteirizacao.${index}.inicio` as const
                    )}
                    error={
                      errors.regrasLoteRoteirizacao?.[index]?.inicio?.message
                    }
                  />
                  <FormInput
                    label="Fim"
                    registration={register(
                      `regrasLoteRoteirizacao.${index}.fim` as const
                    )}
                    error={errors.regrasLoteRoteirizacao?.[index]?.fim?.message}
                  />
                  <FormInput
                    label="Máx. comandas"
                    type="number"
                    min={1}
                    max={3}
                    registration={register(
                      `regrasLoteRoteirizacao.${index}.maxComandas` as const,
                      { valueAsNumber: true }
                    )}
                    error={
                      errors.regrasLoteRoteirizacao?.[index]?.maxComandas
                        ?.message
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="shrink-0 border-t border-neutral-200 bg-white">
        <FormActions onCancel={onCancel} isLoading={isLoading} />
      </div>
    </form>
  );
};

export default LojaForm;
